import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Breadcrumb from '../../components/ui/Breadcrumb';
import CompanyInformation from './components/CompanyInformation';
import SolutionsPortfolio from './components/SolutionsPortfolio';
import TeamExpertise from './components/TeamExpertise';
import PartnershipPreferences from './components/PartnershipPreferences';
import ProfileAnalytics from './components/ProfileAnalytics';
import ProfilePreview from './components/ProfilePreview';

const BusinessProfileManagement = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('company');
  const [businessProfile, setBusinessProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [analytics, setAnalytics] = useState({
    views: 0,
    inquiries: 0,
    connections: 0
  });

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/business-dashboard' },
    { label: 'Business Profile Management' }
  ];

  const tabs = [
    { id: 'company', label: 'Company Information', icon: 'Building' },
    { id: 'solutions', label: 'Solutions Portfolio', icon: 'Package' },
    { id: 'team', label: 'Team & Expertise', icon: 'Users' },
    { id: 'partnerships', label: 'Partnership Preferences', icon: 'Handshake' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'preview', label: 'Preview', icon: 'Eye' }
  ];

  // Fetch business profile and products
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);

        // Fetch business profile
        const { data: business, error: businessError } = await supabase
          ?.from('businesses')
          ?.select('*')
          ?.eq('owner_id', user?.id)
          ?.single();

        if (businessError && businessError?.code !== 'PGRST116') {
          console.error('Error fetching business:', businessError);
        } else if (business) {
          setBusinessProfile(business);
          setAnalytics({
            views: business?.view_count || 0,
            inquiries: Math.floor(Math.random() * 50) + 1, // Mock data
            connections: Math.floor(Math.random() * 25) + 1 // Mock data
          });
        }

        // Fetch products/solutions
        const { data: productsData, error: productsError } = await supabase
          ?.from('products')
          ?.select('*')
          ?.eq('creator_id', user?.id);

        if (productsError) {
          console.error('Error fetching products:', productsError);
        } else if (productsData) {
          setProducts(productsData || []);
        }

      } catch (error) {
        console.error('Error fetching business data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [user?.id]);

  // Calculate profile completeness
  useEffect(() => {
    if (!businessProfile && !userProfile) return;

    const businessFields = [
      businessProfile?.name,
      businessProfile?.description,
      businessProfile?.category,
      businessProfile?.website_url,
      businessProfile?.contact_email,
      businessProfile?.services?.length > 0,
      businessProfile?.technologies?.length > 0,
      businessProfile?.logo_url
    ];

    const userFields = [
      userProfile?.full_name,
      userProfile?.title,
      userProfile?.bio,
      userProfile?.skills?.length > 0,
      userProfile?.linkedin_url
    ];

    const allFields = [...businessFields, ...userFields];
    const completedFields = allFields?.filter(field => Boolean(field))?.length;
    const completenessPercentage = Math.round((completedFields / allFields?.length) * 100);
    
    setProfileCompleteness(completenessPercentage);
  }, [businessProfile, userProfile]);

  const handleSave = async (section, data) => {
    setSaving(true);
    try {
      let result = { error: null };

      if (section === 'company') {
        if (businessProfile?.id) {
          result = await supabase
            ?.from('businesses')
            ?.update(data)
            ?.eq('id', businessProfile?.id)
            ?.select()
            ?.single();
        } else {
          result = await supabase
            ?.from('businesses')
            ?.insert({ ...data, owner_id: user?.id })
            ?.select()
            ?.single();
        }
        
        if (!result?.error) {
          setBusinessProfile(result?.data);
        }
      } else if (section === 'team') {
        result = await supabase
          ?.from('user_profiles')
          ?.update(data)
          ?.eq('id', user?.id)
          ?.select()
          ?.single();
      }

      if (result?.error) {
        console.error(`Error saving ${section}:`, result?.error);
        return { success: false, error: result?.error?.message };
      }

      return { success: true };
    } catch (error) {
      console.error(`Error saving ${section}:`, error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setSaving(false);
    }
  };

  const handleProductSave = async (productData) => {
    setSaving(true);
    try {
      let result = await supabase
        ?.from('products')
        ?.insert({ ...productData, creator_id: user?.id })
        ?.select()
        ?.single();

      if (!result?.error) {
        setProducts(prev => [...(prev || []), result?.data]);
        return { success: true };
      } else {
        console.error('Error saving product:', result?.error);
        return { success: false, error: result?.error?.message };
      }
    } catch (error) {
      console.error('Error saving product:', error);
      return { success: false, error: 'Network error. Please try again.' };
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    const commonProps = {
      businessProfile,
      userProfile,
      products,
      onSave: handleSave,
      onProductSave: handleProductSave,
      saving
    };

    switch (activeTab) {
      case 'company':
        return <CompanyInformation {...commonProps} />;
      case 'solutions':
        return <SolutionsPortfolio {...commonProps} />;
      case 'team':
        return <TeamExpertise {...commonProps} />;
      case 'partnerships':
        return <PartnershipPreferences {...commonProps} />;
      case 'analytics':
        return <ProfileAnalytics businessProfile={businessProfile} analytics={analytics} />;
      case 'preview':
        return <ProfilePreview businessProfile={businessProfile} userProfile={userProfile} products={products} />;
      default:
        return <CompanyInformation {...commonProps} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading business profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} />
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Business Profile Management</h1>
          <p className="mt-2 text-gray-600">
            Create and manage your comprehensive business presence on the smart cities platform.
          </p>
          
          {/* Profile Completeness Indicator */}
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Profile Completeness</h3>
              <span className="text-2xl font-bold text-blue-600">{profileCompleteness}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${profileCompleteness}%` }}
              ></div>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Complete your profile to improve visibility and attract more partnership opportunities.
            </p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{tab?.label}</span>
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfileManagement;